// The Finance page's reads, rules and syncs: ONE copy, used by the desktop Finance page
// (pages/FinancePage.jsx) and the phone (mobile/screens/FinanceScreen.jsx).
// docs/mobile-pwa-plan.md "One copy of every action". Moved from the page; pinned in
// pages/FinancePage.dashboard.actions.test.jsx and pages/FinancePage.pnl.test.jsx. Plaid Link (connect,
// reconnect, add accounts), retiring accounts and the charts stay on the desktop.
import config from '../../config.js';

const API = config.API_BASE_URL;

/** GET/POST a /finance path (the global fetch wrapper adds the API key). Throws `API <status>: <text>`. */
export async function financeFetch(path, opts = {}) {
  const res = await fetch(`${API}${path}`, {
    headers: { 'Content-Type': 'application/json', ...opts.headers },
    ...opts,
  });
  if (!res.ok) throw new Error(`API ${res.status}: ${res.statusText}`);
  return res.json();
}

// --- Dashboard reads -------------------------------------------------------------------------------

/** Active bank / card / loan accounts (GET /finance/accounts). */
export const loadAccounts = () => financeFetch('/finance/accounts').then((r) => r?.data || []);
/** Loans and the API's debt totals (GET /finance/loans): {loans, totals|null}. */
export const loadLoans = () => financeFetch('/finance/loans').then((r) => ({ loans: r?.data || [], totals: r?.totals || null }));
/** Total cash / available across deposit accounts (GET /finance/accounts/cash-position). */
export const loadCashPosition = () => financeFetch('/finance/accounts/cash-position').then((r) => r?.data);
/** Bill.com bills not yet PAID (GET /finance/bills). */
export const loadOpenBills = () => financeFetch('/finance/bills').then((r) => (r?.data || []).filter((b) => b.status !== 'PAID'));
/** The bank connections (GET /finance/plaid/items). */
export const loadPlaidItems = () => financeFetch('/finance/plaid/items').then((r) => r?.data || []);

// --- Syncs (Plaid / Bill.com refreshes; the callers re-read after) ----------------------------------

/** Pull balances from the banks now (POST /finance/accounts/sync). Throws. */
export const syncBalances = () => financeFetch('/finance/accounts/sync', { method: 'POST' });
/** Pull bills from Bill.com now (POST /finance/bills/sync). Throws. */
export const syncBills = () => financeFetch('/finance/bills/sync', { method: 'POST' });
/** Pull transactions from the banks now (POST /finance/transactions/sync). Throws. */
export const syncTransactions = () => financeFetch('/finance/transactions/sync', { method: 'POST' });

// --- Dashboard rules -------------------------------------------------------------------------------

/** Connections whose login broke (status other than active): balances stale until re-authenticated. */
export const brokenPlaidItems = (items) => (items || []).filter((i) => i.status && i.status !== 'active');

/** Healthy connections that still need a person: the bank holds an account we aren't authorized for, or
 *  an account stopped being reported (a payoff or refinance). */
export const accountsToReview = (items) => (items || []).filter(
  (i) => (!i.status || i.status === 'active') && (i.new_accounts_available_at || i.missing_account_count > 0));

/** A loan's balance as shown: the API's resolver (`display_balance`), else the recorded one. */
export const loanBalance = (loan) => Number(loan.display_balance ?? loan.current_balance ?? 0);

/** Debt, counted once (the API owns the reconciliation; local sums only for an older API without totals).
 *  Also which Plaid loan accounts no loan row speaks for (every ***NNNN in a loan's notes claims one). */
export function debtPosition({ accounts = [], loans = [], loanTotals = null }) {
  const loanAccounts = accounts.filter((a) => a.account_type === 'loan');
  const creditAccounts = accounts.filter((a) => a.account_type === 'credit');
  const claimedMasks = new Set(loans.flatMap((l) => [...(l.notes || '').matchAll(/\*\*\*(\d+)/g)].map((m) => m[1])));
  const uniqueLoanAccounts = loanAccounts.filter((a) => !claimedMasks.has(a.mask));
  const totalLoanBalance = loanTotals
    ? Number(loanTotals.debt_loans || 0) + Number(loanTotals.debt_unclaimed_accounts || 0)
    : loans.reduce((s, l) => s + loanBalance(l), 0)
      + uniqueLoanAccounts.reduce((s, a) => s + Math.abs(Number(a.current_balance) || 0), 0);
  const totalMonthly = loanTotals ? Number(loanTotals.monthly_payments || 0)
    : loans.reduce((s, l) => s + (l.monthly_payment || 0), 0);
  const totalCardDebt = Number(loanTotals?.debt_cards ?? creditAccounts.reduce(
    (s, a) => s + Math.abs(Number(a.current_balance) || 0), 0));
  const totalDebt = loanTotals ? Number(loanTotals.total_debt || 0) : totalLoanBalance + totalCardDebt;
  return { loanAccounts, creditAccounts, claimedMasks, uniqueLoanAccounts, totalLoanBalance, totalMonthly, totalCardDebt, totalDebt };
}

/** A bill's amount still owed (the remaining balance, else the amount). */
export const billAmount = (b) => Number(b.remaining_balance || b.amount) || 0;

/** What a scheduled bill will pay: the remaining balance when there is one, else the amount. */
export const scheduledAmount = (b) => (Number(b.remaining_balance) > 0 ? Number(b.remaining_balance) : Number(b.amount) || 0);

/** A bill partly paid: a remaining balance that differs from the amount (the row then shows "of <amount>"). */
export const billPartlyPaid = (b) => !!(b.remaining_balance && b.amount && Number(b.remaining_balance) !== Number(b.amount));

/** Unpaid bills: total owed, the overdue ones (due before today, a calendar day), the scheduled ones and
 *  what they will pay. `now` = the device's today. */
export function billsSummary(bills = [], now = new Date()) {
  const todayMidnight = new Date(now.toDateString());
  const overdueBills = bills.filter((b) => b.due_date && new Date(b.due_date + 'T00:00:00') < todayMidnight);
  const totalAP = bills.reduce((s, b) => s + billAmount(b), 0);
  const scheduledBills = bills.filter((b) => b.status === 'SCHEDULED');
  const scheduledTotal = scheduledBills.reduce((s, b) => s + scheduledAmount(b), 0);
  // The Overdue card's money (the bills' amounts, as the desktop card has always summed them).
  const overdueAmount = overdueBills.reduce((s, b) => s + (Number(b.amount) || 0), 0);
  return { overdueBills, overdueAmount, totalAP, scheduledBills, scheduledTotal };
}

/** Money left after the scheduled bill payments. */
export const netAfterScheduled = (cashPosition, scheduledTotal) =>
  (cashPosition ? (Number(cashPosition.total_available) || 0) : 0) - scheduledTotal;

/** Loan payments due within 7 days and their sum. */
export function loanPaymentsDue(loans = []) {
  const upcoming = loans.filter((l) => l.days_until_payment != null && l.days_until_payment <= 7);
  return { upcoming, total: upcoming.reduce((s, l) => s + (l.monthly_payment || 0), 0) };
}

/** Bank accounts (anything not a loan or a card). */
export const depositAccounts = (accounts = []) => accounts.filter((a) => a.account_type !== 'loan' && a.account_type !== 'credit');

/** Plain names for known Plaid accounts, by mask. */
export const LOAN_LABELS = Object.freeze({
  3795: { name: 'Media Blaster', type: 'Equipment Loan' },
  // 3782 was refinanced into 6480 in July 2026 to fund the building expansion. Kept so the retired loan
  // still reads correctly in history.
  3782: { name: 'Shop Building (refinanced)', type: 'Real Estate' },
  6480: { name: 'Shop Building', type: 'Real Estate' },
  3783: { name: 'Operating LOC', type: 'Line of Credit' },
});
export const CREDIT_LABELS = Object.freeze({
  9270: { name: 'Chase Credit Card' },
});

/** A credit card's figures: balance owed, limit, available (Plaid's remaining credit, else limit − balance),
 *  % of the limit used. */
export function cardFigures(a) {
  const balance = Math.abs(Number(a.current_balance) || 0);
  const limit = Number(a.limit_amount) || 0;
  const plaidAvail = Number(a.available_balance);
  const available = Number.isFinite(plaidAvail) && plaidAvail > 0 ? plaidAvail : (limit > 0 ? Math.max(limit - balance, 0) : null);
  const pctUsed = limit > 0 ? Math.min((balance / limit) * 100, 100) : 0;
  return { label: CREDIT_LABELS[a.mask] || {}, balance, limit, available, pctUsed };
}

/** A loan's figures for its card: the balance shown, % paid (derived from that balance, 0-100), a line of
 *  credit's available credit, the live / stale Plaid account, whether its note is a warning, and the
 *  next-payment wording. */
export function loanFigures(loan, loanAccounts = []) {
  const orig = loan.original_amount || 0;
  const isUrgent = loan.days_until_payment != null && loan.days_until_payment <= 7;
  const isLOC = loan.loan_type === 'line_of_credit';
  const plaidMatch = loan.live_mask ? loanAccounts.find((a) => a.mask === loan.live_mask) : null;
  const stale = loan.stale_mask ? loanAccounts.find((a) => a.mask === loan.stale_mask) : null;
  const displayBalance = loanBalance(loan);
  // Clamped: capitalized closing costs can put the live balance ABOVE the original.
  const pctPaid = Math.max(0, Math.min(orig > 0 ? ((orig - displayBalance) / orig) * 100 : 0, 100));
  const plaidAvail = plaidMatch ? Number(plaidMatch.available_balance) || 0 : 0;
  const locAvailable = isLOC ? (plaidAvail > 0 ? plaidAvail : (orig > displayBalance ? orig - displayBalance : 0)) : 0;
  const notesWarn = /⚠|\bpast due\b|\bnot\b/i.test(loan.notes || '');
  const d = loan.days_until_payment;
  const nextPaymentText = loan.next_payment_date
    ? `Next payment ${d === 0 ? 'today' : d === 1 ? 'tomorrow' : `in ${d}d`} (${loan.next_payment_date})`
    : null;
  return { orig, isUrgent, isLOC, plaidMatch, stale, displayBalance, pctPaid, locAvailable, notesWarn, nextPaymentText };
}

// --- AP aging --------------------------------------------------------------------------------------

export const AGING_BUCKETS = Object.freeze([
  { key: 'current', label: 'Current', color: '#10b981' },
  { key: '1_30', label: '1-30 Days', color: '#fbbf24' },
  { key: '31_60', label: '31-60 Days', color: '#fb923c' },
  { key: '61_90', label: '61-90 Days', color: '#f87171' },
  { key: '90_plus', label: '90+ Days', color: '#ef4444' },
]);

/** A due date's aging bucket: days past due against today's local midnight (no date = current). */
export function getBucket(dueDate, now = new Date()) {
  if (!dueDate) return 'current';
  const today = new Date(now);
  today.setHours(0, 0, 0, 0);
  const due = new Date(dueDate + 'T00:00:00');
  // Rounded, not floored: across a daylight-saving change a calendar day is 23 or 25 hours.
  const diffDays = Math.round((today - due) / 86400000);
  if (diffDays <= 0) return 'current';
  if (diffDays <= 30) return '1_30';
  if (diffDays <= 60) return '31_60';
  if (diffDays <= 90) return '61_90';
  return '90_plus';
}

/** Unpaid bills by bucket ({total, count, bills}), each bucket's vendors (most owed first, with their
 *  bills), and the grand total. */
export function agingReport(bills = [], now = new Date()) {
  const buckets = {};
  AGING_BUCKETS.forEach((b) => { buckets[b.key] = { total: 0, count: 0, bills: [] }; });
  bills.forEach((bill) => {
    const bucket = getBucket(bill.due_date, now);
    buckets[bucket].total += billAmount(bill);
    buckets[bucket].count++;
    buckets[bucket].bills.push(bill);
  });
  const vendorsByBucket = {};
  AGING_BUCKETS.forEach((b) => {
    const vm = {};
    buckets[b.key].bills.forEach((bill) => {
      const name = bill.vendor_name || 'Unknown';
      if (!vm[name]) vm[name] = { total: 0, count: 0, bills: [] };
      vm[name].total += billAmount(bill);
      vm[name].count++;
      vm[name].bills.push(bill);
    });
    vendorsByBucket[b.key] = Object.entries(vm).map(([name, data]) => ({ name, ...data })).sort((x, y) => y.total - x.total);
  });
  const grandTotal = Object.values(buckets).reduce((s, b) => s + b.total, 0);
  return { buckets, vendorsByBucket, grandTotal };
}

/** A bill's status as the badge reads it ("PARTIALLY PAID"). */
export const billStatusLabel = (status) => String(status || '').replace(/_/g, ' ');

/** A bill date as "Oct 3, 2026" (a calendar day); "—" for none. */
export function fmtBillDate(d) {
  if (!d) return '—';
  return new Date(d + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

// --- P&L -------------------------------------------------------------------------------------------

export const PNL_RANGES = Object.freeze([
  { key: '3mo', label: '3mo', query: 'months=3' },
  { key: '6mo', label: '6mo', query: 'months=6' },
  { key: '12mo', label: '12mo', query: 'months=12' },
  { key: 'ytd', label: 'YTD', query: 'period=ytd' },
]);
export const pnlQuery = (rangeKey) => (PNL_RANGES.find((r) => r.key === rangeKey) || PNL_RANGES[3]).query;

/** GET /finance/reports/pnl?{query} → the report (months, totals, basis, coverage) or undefined. */
export const loadPnl = (query) => financeFetch(`/finance/reports/pnl?${query}`).then((r) => r?.data);
/** One line's categories (revenue | invoiced | opex | interest). */
export const loadPnlBreakdown = (line, query) =>
  financeFetch(`/finance/reports/pnl/breakdown?line=${line}&${query}`).then((r) => r?.data?.categories || []);
/** One category's transactions. */
export const loadPnlRows = (line, key, query) =>
  financeFetch(`/finance/reports/pnl/transactions?line=${line}&key=${encodeURIComponent(key)}&${query}`).then((r) => r?.data?.transactions || []);

/** The P&L's lines in order, as the desktop table lists them: {field, label, drill (the line a tap opens,
 *  or null), tone ('in' | 'out' | 'interest' | 'muted' | 'net'), bold, muted}. On a cash-basis report
 *  Revenue / Invoiced / Operating Expenses open their categories; Loan interest always does. */
export function pnlLines(pnl) {
  const isCash = pnl?.basis === 'cash';
  const totals = pnl?.totals || {};
  const lines = [
    { field: 'revenue', label: isCash ? 'Revenue (cash collected)' : 'Revenue', drill: isCash ? 'revenue' : null, tone: 'in', bold: true },
  ];
  if (isCash) {
    lines.push({ field: 'collected_offset', label: 'of which netted with Bad Idea', drill: null, tone: 'muted', muted: true });
    lines.push({ field: 'invoiced', label: 'Invoiced (for reference)', drill: 'invoiced', tone: 'muted', muted: true });
  }
  lines.push({ field: 'opex', label: 'Operating Expenses', drill: isCash ? 'opex' : null, tone: 'out' });
  if (totals.opex_billcom !== undefined) lines.push({ field: 'opex_billcom', label: 'Bill.com bills (by date paid)', drill: null, tone: 'muted', muted: true });
  if (totals.opex_bank !== undefined) lines.push({ field: 'opex_bank', label: 'Paid outside Bill.com (checking, card)', drill: null, tone: 'muted', muted: true });
  if (totals.interest !== undefined) lines.push({ field: 'interest', label: 'Loan interest', drill: 'interest', tone: 'interest' });
  lines.push({ field: 'net_profit', label: 'Net Profit', drill: null, tone: 'net', bold: true });
  return lines.map((l) => ({ bold: false, muted: false, ...l }));
}

/** The report's coverage notes worth showing (the loan-estimate note is said on the line itself). */
export const pnlNotes = (pnl) => (pnl?.coverage?.notes || []).filter((n) => !/estimated from each loan/.test(n));

// --- Transactions ----------------------------------------------------------------------------------

export const PAGE_SIZE = 50;

/** GET /finance/transactions params for a page and the filters. */
export function transactionsParams({ page = 0, search = '', startDate = '', endDate = '', accountId = '' } = {}) {
  const params = new URLSearchParams();
  params.set('skip', String(page * PAGE_SIZE));
  params.set('limit', String(PAGE_SIZE));
  if (search) params.set('merchant', search);
  if (startDate) params.set('start_date', startDate);
  if (endDate) params.set('end_date', endDate);
  if (accountId) params.set('account_id', accountId);
  return params;
}

/** One page of transactions: {rows, total|null}. */
export const loadTransactions = (filters) =>
  financeFetch(`/finance/transactions?${transactionsParams(filters)}`).then((r) => ({ rows: r?.data || [], total: r?.total ?? null }));

/** Plaid's sign: a positive amount is money OUT. */
export const isMoneyOut = (t) => Number(t.amount) > 0;

/** Sort one loaded page (date desc by default; amounts as numbers; blanks last). A new array. */
export function sortTransactions(list, field = 'date', dir = 'desc') {
  return [...(list || [])].sort((a, b) => {
    let aVal = a[field];
    let bVal = b[field];
    if (field === 'amount') { aVal = Number(aVal) || 0; bVal = Number(bVal) || 0; }
    if (aVal == null) return 1;
    if (bVal == null) return -1;
    if (aVal < bVal) return dir === 'asc' ? -1 : 1;
    if (aVal > bVal) return dir === 'asc' ? 1 : -1;
    return 0;
  });
}

/** "x–y of N" for a page. */
export function pageRange(page, total) {
  return {
    totalPages: Math.ceil(total / PAGE_SIZE),
    rangeStart: total > 0 ? page * PAGE_SIZE + 1 : 0,
    rangeEnd: Math.min((page + 1) * PAGE_SIZE, total),
  };
}
